import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7eceecc6"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "posWrapper" }
const _hoisted_2 = { class: "area-control-ct" }
const _hoisted_3 = { class: "tools-btn-ct" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "divider" }, null, -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "divider" }, null, -1))

import MapboxDraw, { DrawCreateEvent, DrawSelectionChangeEvent } from '@mapbox/mapbox-gl-draw';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { DrawTool } from './Map.vue';
import { CircleMode, DragCircleMode, DirectMode, SimpleSelectMode } from 'mapbox-gl-draw-circle';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import toGeoJson from '@mapbox/togeojson';
import JSZip from 'jszip';
import fileExtension from 'file-extension';
import BaseMapControls from '@/components/BaseMapControls.vue';
import truncate from '@turf/truncate';
import bbox from '@turf/bbox';
import area from '@turf/area';
// area of polygon calcuation turf import
import polygon from 'turf-polygon';
import ReportModal from './ReportModal.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'DrawToolCard',
  props: {
    map: null
  },
  emits: ["onDrawUpdate", "onSelectionChange", "onDrawCreate", "onDrawDelete", "onLayerToggle", "onLocationCentered"],
  setup(__props: any, { expose, emit }: { emit: ({
  // (e: 'onToolChange', tool: DrawTool): void;
  (e: 'onDrawUpdate', event: { feature: any; shape: DrawTool }): void;
  (e: 'onSelectionChange', event: { feature: any; shape: DrawTool }): void;
  (e: 'onDrawCreate', event: { feature: any; shape: DrawTool }): void;
  (e: 'onDrawDelete', event: { feature: any; shape: DrawTool }): void;
  (e: 'onLayerToggle'): void;
  (e: 'onLocationCentered', coord: { lng: number; lat: number }): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { map: mapboxgl.Map }


const kmlInput = ref<HTMLInputElement>();

const activeAreaMode = ref<DrawTool>();
const isDrawing = ref(false);
const snackbar = ref<{
  show: boolean;
  text?: string;
  status: 'error' | 'success' | 'info';
  vertical?: boolean;
}>({
  show: false,
  status: 'info',
  vertical: true,
});

const draw = ref<MapboxDraw>(
  new MapboxDraw({
    displayControlsDefault: false,
    userProperties: true,
    modes: {
      ...MapboxDraw.modes,
      draw_rectangle: DrawRectangle,
      draw_circle: CircleMode,
      drag_circle: DragCircleMode,
      direct_select: DirectMode,
      simple_select: SimpleSelectMode,
    },
  })
);
const toggleLayer = () => emit('onLayerToggle');

function updateMode(tool: DrawTool) {
  let mode;
  switch (tool) {
    case 'circle':
      mode = 'drag_circle';
      // setting cursor style to crosshair
      props.map.getContainer().classList.toggle('mouse-add');
      break;
    case 'rectangle':
      mode = 'draw_rectangle';
      break;
    case 'polygon':
      mode = 'draw_polygon';
      break;
    default:
      mode = 'simple_select';
      break;
  }

  if (activeAreaMode.value !== tool) {
    draw.value.changeMode(mode as any);
    isDrawing.value = true;
    return tool;
  } else {
    draw.value.changeMode('simple_select');
    isDrawing.value = false;
    return undefined;
  }
}

const onToolChange = (tool: DrawTool) => {
  activeAreaMode.value = updateMode(tool);
};

const onKeyDown = (e: KeyboardEvent): void => {
  if (e.key === 'Delete') {
    deleteSelectedDrawing();
  }
};

const deleteSelectedDrawing = () => {
  // delete selected drawing
  draw.value.delete(draw.value.getSelectedIds());
};

const centerMap = () => {
  // Center the map based on the user's location.
  (navigator?.geolocation as Geolocation)?.getCurrentPosition?.(
    (currGeoLoc) => {
      emit('onLocationCentered', {
        lng: currGeoLoc.coords.longitude,
        lat: currGeoLoc.coords.latitude,
      });
      props.map.flyTo({
        center: [currGeoLoc.coords.longitude, currGeoLoc.coords.latitude],
        zoom: 10,
      });
    },
    (error) => {
      // Asking for permissions again, if disabled.
      if (error.code === error.PERMISSION_DENIED) {
        snackbar.value = {
          show: true,
          text: 'Please enable location permission for this functionality.',
          status: 'error',
        };
      }
    }
  );
};

const removePreviousDrawnShape = () => {
  // remove the previous drawn shape
  const previousShape = draw.value.getAll().features[0];
  draw.value.delete(previousShape.id as string);
};

const readKmlFile = async (e: Event) => {
  const fileInput = e.target as HTMLInputElement;

  if (fileInput.files.length === 0) {
    return;
  }

  if (draw.value.getAll().features.length >= 1) {
    removePreviousDrawnShape();
  }

  const extension = fileExtension(fileInput.files[0].name);
  let xml, geojson, kml;
  if (extension === 'kml' || extension === 'geojson') {
    const file = await new Promise<ProgressEvent<FileReader>>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = (e) => {
        resolve(e);
      };
      fr.onerror = reject;
      fr.readAsText(fileInput.files[0]);
    });

    if (extension === 'geojson') {
      geojson = JSON.parse(file.target.result as string);
    } else {
      xml = file.target.result as string;
      kml = new DOMParser().parseFromString(xml, 'text/xml');
      // converting kml to geojson then coverting that into 2d coordinates
      geojson = truncate(toGeoJson.kml(kml), { coordinates: 2 });
    }
  } else if (extension === 'kmz') {
    const zip = await JSZip.loadAsync(fileInput.files[0]);
    let kmlFile: JSZip.JSZipObject;

    zip.forEach((relPath, file) => {
      // Safety check to ensure that the processing is done only for kml files
      // and only first kml file
      if (fileExtension(relPath) === 'kml' && !kmlFile) {
        kmlFile = file;
        return;
      }
    });

    if (kmlFile === null) {
      // processKMLFeatures(features);
      snackbar.value = { show: true, text: 'No kml file found in the zip', status: 'error' };
      return;
    }

    xml = await kmlFile.async('string');
    kml = new DOMParser().parseFromString(xml, 'text/xml');
    // converting kml to geojson then coverting that into 2d coordinates
    geojson = truncate(toGeoJson.kml(kml), { coordinates: 2 });
  }

  const features = geojson.features;

  if (features.length === 0) {
    snackbar.value = { show: true, text: 'There are no feature to display', status: 'error' };
  }

  //   Find if any polygons exists in the features file, if there is even 1 polygon, display it, otherwise, error.
  let idx = -1;

  //   Use reduce function to implement following for loop.
  features.reduce((accumulator, currentValue, i) => {
    if (currentValue.geometry.type === 'Polygon') {
      idx = i;
      // Grabbing only the first polygon.
      return true;
    }
    return false;
  }, false);

  for (let i = 0; i < features.length; i++) {
    const element = features[i];
    if (element.geometry.type === 'Polygon') {
      idx = i;
      // Grabbing only the first polygon.
      break;
    }
  }

  //   If there are 1 or more than 1 polygons, just display the first one.
  if (idx == -1) {
    snackbar.value = {
      show: true,
      text: 'Only polygons are supported in the KML file',
      status: 'error',
    };
    return;
  }

  const geometry = features[idx].geometry;

  //   Calculating the area of the polygon using turf, and if it is too big, return error.
  const coordinates = geometry.coordinates;
  const turfPolygon = polygon(coordinates);
  const polygonArea = area(turfPolygon);

  //   The threshold is 100 sq km, the unit is in sq metres, i.e 100000000 sq.m = 100 sq km.
  if (polygonArea >= 100000000) {
    snackbar.value = {
      show: true,
      text: 'The uploaded polygon is too big, please select a polygon with smaller area.',
      status: 'error',
    };
    return;
  }

  // highlighting polygon tool
  // onToolChange('polygon');

  const addedFeature = draw.value.add(geometry);
  draw.value.setFeatureProperty(addedFeature[0], 'shape', 'polygon');
  emit('onDrawCreate', { feature: features[idx], shape: 'polygon' });
  snackbar.value = {
    show: true,
    text: 'KML/GeoJSON File uploaded successfully',
    status: 'success',
  };

  if (features.length >= 2) {
    snackbar.value = {
      show: true,
      text: 'Multiple polygons detected in file, displaying only first one.',
      status: 'info',
    };
  }

  const boundingBox = bbox(turfPolygon);
  // zoom to fit polygon
  props.map.fitBounds(boundingBox as any, { padding: 200 });

  draw.value.changeMode('simple_select', { featureIds: addedFeature });
  fileInput.value = null;
};

const onShapeCreated = (e: DrawCreateEvent) => {
  if (draw.value.getAll().features.length > 1) {
    removePreviousDrawnShape();
  }

  // after creating rectangle highlighting the shape selected
  const featureId = e.features[0].id as string;

  if (activeAreaMode.value === 'rectangle') {
    nextTick(() => {
      draw.value.changeMode('simple_select', { featureIds: [featureId] });
    });
  }

  draw.value.setFeatureProperty(featureId, 'shape', activeAreaMode.value);

  emit('onDrawCreate', {
    feature: e.features[0],
    shape: activeAreaMode.value,
  });

  activeAreaMode.value = undefined;
  // reseting the cursor style to default after finish drawing
  props.map.getContainer().classList.remove('mouse-add');
  isDrawing.value = false;
};

const onShapeUpdated = (e) => {
  emit('onDrawUpdate', {
    feature: e.features[0],
    shape: e.features[0].properties.shape,
  });
};

const onShapeDelete = (e) => {
  emit('onDrawDelete', {
    feature: e.features[0],
    shape: e.features[0].properties.shape,
  });
};

const onSelectedShapeChange = (e: DrawSelectionChangeEvent) => {
  if (e.features.length > 0) {
    emit('onSelectionChange', {
      feature: e.features[0],
      shape: e.features[0].properties.shape,
    });
  }
};

onMounted(() => {
  // Adding drawing tools to mapbox instance
  props.map.addControl(draw.value);

  // Re-emitting events from mapboxDraw
  props.map.on('draw.create', onShapeCreated);
  props.map.on('draw.update', onShapeUpdated);
  props.map.on('draw.delete', onShapeDelete);

  props.map.on('draw.selectionchange', onSelectedShapeChange);

  // Listing for delete key press to delete selected feature
  window.addEventListener('keydown', onKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown);
});
expose({ draw, isDrawing, deleteSelectedDrawing });

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_VSnackbar = _resolveComponent("VSnackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_v_tooltip, {
          text: "Zoom In",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("button", _mergeProps(props, {
              class: "area-btn zoomBtn",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (__props.map.zoomIn()))
            }), [
              _createVNode(_component_Icon, {
                class: "icon",
                icon: "mdi:plus"
              })
            ], 16)
          ]),
          _: 1
        }),
        _createVNode(_component_v_tooltip, {
          text: "Zoom Out",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("button", _mergeProps(props, {
              class: "area-btn zoomBtn",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (__props.map.zoomOut()))
            }), [
              _createVNode(_component_Icon, {
                class: "icon",
                icon: "mdi:minus"
              })
            ], 16)
          ]),
          _: 1
        }),
        _hoisted_4,
        _createVNode(_component_v_tooltip, {
          text: "Center Map",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("button", _mergeProps(props, {
              class: "area-btn",
              onClick: centerMap
            }), [
              _createVNode(_component_Icon, {
                class: "icon",
                icon: "mdi:crosshairs-gps"
              })
            ], 16)
          ]),
          _: 1
        }),
        _createVNode(_component_v_tooltip, {
          text: "Draw Polygon",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("button", _mergeProps(props, {
              class: [[{ active: activeAreaMode.value == 'polygon' }], "area-btn"],
              onClick: _cache[2] || (_cache[2] = ($event: any) => (onToolChange('polygon')))
            }), [
              _createVNode(_component_Icon, {
                class: "icon",
                icon: "mdi:vector-polygon"
              })
            ], 16)
          ]),
          _: 1
        }),
        _createVNode(_component_v_tooltip, {
          text: "Delete Shape",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("button", _mergeProps(props, {
              class: "area-btn",
              onClick: deleteSelectedDrawing
            }), [
              _createVNode(_component_Icon, {
                class: "icon",
                icon: "mdi:trash-can-outline"
              })
            ], 16)
          ]),
          _: 1
        }),
        _hoisted_5,
        _createVNode(_component_v_tooltip, {
          text: "Upload KML/KMZ",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("input", {
              ref_key: "kmlInput",
              ref: kmlInput,
              type: "file",
              name: "kml",
              accept: ".kml,.kmz,.geojson",
              id: "kml-input",
              hidden: "",
              onChange: readKmlFile
            }, null, 544),
            _createElementVNode("button", _mergeProps(props, {
              class: "area-btn",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (kmlInput.value.click()))
            }), [
              _createVNode(_component_Icon, {
                class: "icon",
                icon: "mdi:upload-outline"
              })
            ], 16)
          ]),
          _: 1
        }),
        _createVNode(_component_v_tooltip, {
          text: "Toggle Layers",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createElementVNode("button", _mergeProps(props, {
              class: "area-btn",
              onClick: toggleLayer
            }), [
              _createVNode(_component_Icon, {
                class: "icon",
                icon: "mdi:layers-outline"
              })
            ], 16)
          ]),
          _: 1
        }),
        _createVNode(_component_v_tooltip, {
          text: "Report an Issue",
          location: "left"
        }, {
          activator: _withCtx(({ props }) => [
            _createVNode(ReportModal, { draw: draw.value }, {
              default: _withCtx(({ modalProp }) => [
                _createElementVNode("button", _mergeProps({ ...props, ...modalProp }, { class: "area-btn" }), [
                  _createVNode(_component_Icon, {
                    class: "icon",
                    icon: "mdi:comment-alert-outline"
                  })
                ], 16)
              ]),
              _: 2
            }, 1032, ["draw"])
          ]),
          _: 1
        })
      ]),
      _createVNode(_component_VSnackbar, {
        location: "top center",
        class: _normalizeClass(snackbar.value.status),
        modelValue: snackbar.value.show,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((snackbar.value.show) = $event))
      }, {
        actions: _withCtx(() => [
          _createElementVNode("button", {
            class: "v-snackbar-close-btn",
            onClick: _cache[4] || (_cache[4] = ($event: any) => (snackbar.value.show = false))
          }, [
            _createVNode(_component_Icon, { icon: "mdi:close" })
          ])
        ]),
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(snackbar.value?.text) + " ", 1)
        ]),
        _: 1
      }, 8, ["class", "modelValue"])
    ]),
    _createVNode(BaseMapControls, {
      map: props.map
    }, null, 8, ["map"])
  ]))
}
}

})